import Vue from 'vue';

/*
 * Глобальная регистрация означает «попадёт в стартовый бандл». Замер по отчёту
 * сборки: из-за неё в app.js приезжали three.js (1276 КБ), vue-slick-carousel
 * (328 КБ) и все секции лендингов — на каждой странице сайта, включая те, где
 * ни одна из них не рисуется.
 *
 * Поэтому здесь два списка:
 *
 *   1. Обычные импорты — мелкие компоненты, которые есть почти на любом экране
 *      и участвуют в первой отрисовке. Загружать их отдельным запросом дороже,
 *      чем везти в бандле.
 *   2. Отложенные (() => import(...)) — тяжёлые или редкие. Vue 2 понимает
 *      такую фабрику и грузит компонент в тот момент, когда он впервые
 *      понадобился в разметке. webpackChunkName собирает их в общие чанки,
 *      чтобы вместо тридцати запросов получилось три.
 *
 * Секции лендингов рисуются по данным из API, то есть заведомо позже первого
 * кадра, — отложенная загрузка не добавляет им задержки, которой не было бы.
 */

// --- ядро: мелкое и нужно почти везде -------------------------------------

import Loader from '@/components/misc/loader.vue';
import smallLoader from '@/components/misc/small-loader.vue';
import buttonLoader from '@/components/misc/button-loader.vue';
import searchLoader from '@/components/misc/search-loader.vue';
import circleLoader from '@/components/misc/circle-loader.vue';
import PopupWrap from '@/components/misc/popup-wrap.vue';
import mainButton from '@/components/main-button/index.vue';
import allResult from '@/components/all-result/index.vue';
import breadcrumbs from '@/components/breadcrumbs/index.vue';
import mainSelect from '@/components/main-select/index.vue';
import productCard from '@/components/product-card/index.vue';
import productCardLoading from '@/components/product-card-loading/index.vue';
import catalogCategoryCard from '@/components/catalog-category-card/index.vue';

Vue.component('loader', Loader);
Vue.component('small-loader', smallLoader);
Vue.component('button-loader', buttonLoader);
Vue.component('search-loader', searchLoader);
Vue.component('circle-loader', circleLoader);
Vue.component('popup-wrap', PopupWrap);
Vue.component('main-button', mainButton);
Vue.component('all-result', allResult);
Vue.component('breadcrumbs', breadcrumbs);
Vue.component('main-select', mainSelect);
Vue.component('product-card', productCard);
Vue.component('product-card-loading', productCardLoading);
Vue.component('catalog-category-card', catalogCategoryCard);

// --- первый экран лендингов: баннер, обложка, текст ------------------------
// Эти секции стоят наверху страницы, поэтому едут вместе с бандлом: лишний
// запрос за ними отодвинул бы самый крупный видимый элемент.

import banner from '@/components/sections/banner/index.vue';
import mainBanner from '@/components/main-banner/index.vue';
import mainSlider from '@/components/sections/main-slider/index.vue';
import cover from '@/components/sections/cover/index.vue';
import seoText from '@/components/sections/seo-text/index.vue';
import oneText from '@/components/sections/one-text/index.vue';
import description from '@/components/sections/description/index.vue';

Vue.component('banner', banner);
Vue.component('main-banner', mainBanner);
Vue.component('main-slider', mainSlider);
Vue.component('cover', cover);
Vue.component('seo-text', seoText);
Vue.component('one-text', oneText);
Vue.component('description', description);

// --- секции лендингов ниже первого экрана ---------------------------------

Vue.component('articles',              () => import(/* webpackChunkName: "sections" */ '@/components/sections/articles/index.vue'));
Vue.component('badges',                () => import(/* webpackChunkName: "sections" */ '@/components/sections/badges/index.vue'));
Vue.component('before-after-slider',   () => import(/* webpackChunkName: "sections" */ '@/components/sections/before-after-slider/index.vue'));
Vue.component('call-back',             () => import(/* webpackChunkName: "sections" */ '@/components/sections/call-back/index.vue'));
Vue.component('category-list',         () => import(/* webpackChunkName: "sections" */ '@/components/sections/category-list/index.vue'));
Vue.component('checked-items',         () => import(/* webpackChunkName: "sections" */ '@/components/sections/checked-items/index.vue'));
Vue.component('color',                 () => import(/* webpackChunkName: "sections" */ '@/components/sections/color/index.vue'));
Vue.component('color-palate',          () => import(/* webpackChunkName: "sections" */ '@/components/sections/color-palate/index.vue'));
Vue.component('faq-section',           () => import(/* webpackChunkName: "sections" */ '@/components/sections/faq-section/index.vue'));
Vue.component('image-text',            () => import(/* webpackChunkName: "sections" */ '@/components/sections/image-text/index.vue'));
Vue.component('mirrored-section',      () => import(/* webpackChunkName: "sections" */ '@/components/sections/mirrored-section/index.vue'));
Vue.component('mono-section',          () => import(/* webpackChunkName: "sections" */ '@/components/sections/mono/index.vue'));
Vue.component('order-product',         () => import(/* webpackChunkName: "sections" */ '@/components/sections/order-product/index.vue'));
Vue.component('partner',               () => import(/* webpackChunkName: "sections" */ '@/components/sections/partner/index.vue'));
Vue.component('picture-under-picture', () => import(/* webpackChunkName: "sections" */ '@/components/sections/picture-under-picture/index.vue'));
Vue.component('products',              () => import(/* webpackChunkName: "sections" */ '@/components/sections/products/index.vue'));
Vue.component('simple-items',          () => import(/* webpackChunkName: "sections" */ '@/components/sections/simple-items/index.vue'));
Vue.component('slider-badge',          () => import(/* webpackChunkName: "sections" */ '@/components/sections/slider-badge/index.vue'));
Vue.component('slider-center-mode',    () => import(/* webpackChunkName: "sections" */ '@/components/sections/slider-center-mode/index.vue'));
Vue.component('slider-items',          () => import(/* webpackChunkName: "sections" */ '@/components/sections/slider-items/index.vue'));
Vue.component('slider-text',           () => import(/* webpackChunkName: "sections" */ '@/components/sections/slider-text/index.vue'));
Vue.component('solution-slider',       () => import(/* webpackChunkName: "sections" */ '@/components/sections/solution-slider/index.vue'));
Vue.component('table-section',         () => import(/* webpackChunkName: "sections" */ '@/components/sections/table/index.vue'));
Vue.component('text-slider',           () => import(/* webpackChunkName: "sections" */ '@/components/sections/text-slider/index.vue'));
Vue.component('two-images',            () => import(/* webpackChunkName: "sections" */ '@/components/sections/two-images/index.vue'));
Vue.component('warmhouses',            () => import(/* webpackChunkName: "sections" */ '@/components/sections/warmhouses/index.vue'));

// --- медиа и калькуляторы: самое тяжёлое ----------------------------------
// Плеер и видеосекции тянут за собой слайдер, калькулятор кровли — свою
// геометрию и таблицы. На большинстве страниц не нужны вовсе.

Vue.component('video-player', () => import(/* webpackChunkName: "media" */ '@/components/video-player/index.vue'));
Vue.component('video-slider', () => import(/* webpackChunkName: "media" */ '@/components/sections/video-slider/index.vue'));
Vue.component('video-list', () => import(/* webpackChunkName: "media" */ '@/components/sections/video-list/index.vue'));

Vue.component('roof-calculation', () => import(/* webpackChunkName: "roof-calculation" */ '@/modules/product-card/pages/roof-calculation/index.vue'));
Vue.component('option-reliz', () => import(/* webpackChunkName: "roof-calculation" */ '@/modules/product-card/pages/roof-calculation/components/option/index.vue'));

Vue.component('timer', () => import(/* webpackChunkName: "timer" */ '@/components/timer/index.vue'));

/*
 * Удалены как мёртвые: title-video (компонент есть, в разметке не встречается),
 * colour-picker, vue-gallery, vue-simple-range-slider и v-pagination
 * (последний регистрировался из window['vue-plain-pagination'], которого в
 * проекте нет, — то есть всегда был undefined).
 *
 * Вместе с ними из бандла ушли пакеты vue-colour-picker, vue-gallery
 * с blueimp-gallery, vue-simple-range-slider, vue-product-zoomer,
 * @mathieustan/vue-datepicker и vue-lazyload — ни один не использовался
 * ни в одном шаблоне.
 */

// --- плагины --------------------------------------------------------------

import VueCarousel from 'vue-carousel';
import VueObserveVisibility from 'vue-observe-visibility';
import {InlineSvgPlugin} from 'vue-inline-svg';
import Vuelidate from 'vuelidate';
import VueTheMask from 'vue-the-mask';
import VueHead from 'vue-head';
import * as VueAos from 'vue-aos';
import infiniteScroll from 'vue-infinite-scroll';
import VueSmoothScroll from 'vue2-smooth-scroll';

Vue.use(VueHead);
Vue.use(infiniteScroll);
Vue.use(VueAos);
Vue.use(VueTheMask);
Vue.use(Vuelidate);
Vue.use(VueObserveVisibility);
Vue.use(VueCarousel);
Vue.use(InlineSvgPlugin);

Vue.use(VueSmoothScroll, {
    duration: 700,
    updateHistory: false,
    offset: 0,
});
